import { useModeFilter } from '@src/hooks';
import { useCustomerPricingTierContext } from '@src/providers';
import {
  Box,
  Button,
  ButtonVariants,
  CustomDrawerFieldController,
  EntityAdmin,
  Flex,
  HStack,
  MultiSelect,
  useBasePairFilter,
  useConstant,
  useCustomDrawer,
  useEffectOnce,
  useSymbolCurrencyFilter,
  type BlotterTableFiltersProps,
  type FilterableProperty,
  type InputsAndDropdownsDrawerDropdownOption,
  type SymbolGroup,
} from '@talos/kyoko';
import { getSecurityDrawerOptions } from '@talos/kyoko/src/components/EntityAdmin/components/InputsAndDropdownsDrawer/utils';
import { compact, isNil } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { getSymbolGroupsPatchDeletePath, SYMBOL_GROUPS_SYMBOLS_COLUMNS, useFilteredCustomerSecurities } from './utils';

export const SymbolGroupChildrenDrawer = () => {
  const { originalEntity } = useCustomDrawer<SymbolGroup>();
  const { addSymbolsToSymbolGroup, getSymbolsForSymbolGroup } = useCustomerPricingTierContext();
  const [selectedSymbols, setSelectedSymbols] = useState<{ value: string; label: string; description?: string }[]>([]);
  const filteredSecurities = useFilteredCustomerSecurities();
  const [refreshKey, setRefreshKey] = useState(0);
  const [existingSymbols, setExistingSymbols] = useState<string[]>([]);
  const [isLoadingExistingSymbols, setIsLoadingExistingSymbols] = useState(false);
  const modeFilter = useModeFilter();
  const currencyBaseQuoteOfSymbolFilter = useSymbolCurrencyFilter(filteredSecurities);
  const basePairFilter = useBasePairFilter(filteredSecurities);

  const blotterTableFiltersProps: Partial<BlotterTableFiltersProps> = useConstant({
    suppressClearAllFiltersButton: true,
  });

  const filterableProperties: FilterableProperty[] | undefined = useMemo(() => {
    const properties = [modeFilter, currencyBaseQuoteOfSymbolFilter, basePairFilter];
    // As per the Entity Admin API, undefined is used to indicate a loading state
    return properties.every(prop => !isNil(prop)) ? properties : undefined;
  }, [modeFilter, currencyBaseQuoteOfSymbolFilter, basePairFilter]);

  const updateExistingSymbols = useCallback(async () => {
    setIsLoadingExistingSymbols(true);
    if (!originalEntity?.Tier) {
      setExistingSymbols([]);
      return;
    }
    getSymbolsForSymbolGroup(originalEntity.Tier)
      .then(({ data }) => setExistingSymbols(compact(data.map(sg => sg.Symbol))))
      .catch(() => setExistingSymbols([]))
      .finally(() => setIsLoadingExistingSymbols(false));
  }, [originalEntity?.Tier, getSymbolsForSymbolGroup]);

  // Initial fetch of existing symbols for the symbol group
  useEffectOnce(() => {
    updateExistingSymbols();
  });

  const handleAddSymbols = useCallback(async () => {
    if (!originalEntity?.Tier || selectedSymbols.length === 0) {
      return;
    }
    await addSymbolsToSymbolGroup(
      originalEntity.Tier,
      selectedSymbols.map(s => s.value)
    ).finally(() => {
      setRefreshKey(prev => prev + 1);
      setSelectedSymbols([]);
      updateExistingSymbols();
    });
  }, [originalEntity?.Tier, selectedSymbols, addSymbolsToSymbolGroup, updateExistingSymbols]);

  const multiSelectOptions = useMemo(() => {
    if (isLoadingExistingSymbols) {
      return [];
    }
    return getSecurityDrawerOptions(filteredSecurities).filter(
      option => !selectedSymbols.some(s => s.value === option.value) && !existingSymbols.includes(option.value)
    );
  }, [isLoadingExistingSymbols, filteredSecurities, selectedSymbols, existingSymbols]);

  if (originalEntity == null) {
    // Workflow: Adding a new Symbol Group
    return (
      <Box flex={1} m="spacing12">
        <CustomDrawerFieldController name="Tier" />
      </Box>
    );
  }

  const addSymbolsHeader = (
    <HStack background="backgroundDrawer" w="100%" justifyContent="space-between" p="spacing12" gap="spacing12">
      <Flex w="100%">
        <MultiSelect<InputsAndDropdownsDrawerDropdownOption<string>>
          options={multiSelectOptions}
          selections={selectedSymbols}
          onChange={setSelectedSymbols}
          getLabel={option => option.label}
          getDescription={option => option.description ?? ''}
          placeholder="Select symbols..."
          style={{ width: '100%' }}
          clearable={true}
          disabled={isLoadingExistingSymbols}
        />
      </Flex>
      <Button variant={ButtonVariants.Positive} onClick={handleAddSymbols} disabled={selectedSymbols.length === 0}>
        Add Symbols
      </Button>
    </HStack>
  );

  return (
    <EntityAdmin<SymbolGroup>
      key={refreshKey} // Force a re-render of this EntityAdmin when new symbols are added
      path={`/symbol-groups/${originalEntity.Tier}/symbols`}
      pageSubHead={addSymbolsHeader}
      getPatchPath={getSymbolGroupsPatchDeletePath}
      columns={SYMBOL_GROUPS_SYMBOLS_COLUMNS}
      entityIDField="Symbol"
      entityName="Symbol"
      allowDeleteEntity={true}
      allowModeSwitch={true}
      filterableProperties={filterableProperties}
      blotterTableFiltersProps={blotterTableFiltersProps}
      allowBulkEdit={true}
      suppressBulkDelete={true}
    />
  );
};
